/**
 * Signals — the county's nervous system.
 *
 * A signal is a fact that already happened. Systems state facts; they do not
 * call each other. `death.ts` should never have to know that Ember exists, and
 * `factions.ts` should never have to import the Hollow.
 *
 * Two ways to raise one:
 *
 *   1. `emit(w, "extract", { depth })` — explicit, from the system that knows.
 *   2. Change authoritative state and let `observe()` derive it. Dying,
 *      descending, extracting, and the weather turning are all already recorded
 *      in WorldState, so no module has to remember to announce them.
 *
 * (2) exists so an agent working inside their own file can cause a world
 * reaction without editing anyone else's. Prefer (1) when the fact carries
 * detail that a state delta cannot — who gave what to whom, which line was read.
 *
 * Signals are drained every tick. They are not history; `w.log` is history.
 */
import type { LiveWorld, Signal, SignalKind, WorldState } from "./types";

/** A frame that produces more than this is a bug, not a busy day. */
const MAX_QUEUE = 64;

/**
 * Backfill the cross-system fields on a world that predates them — a fresh
 * `createWorld()`, or a save written before this layer existed. Cheap and
 * idempotent; call it anywhere you need the layer to be real.
 */
export function ensureSystems(w: WorldState): LiveWorld {
  const l = w as LiveWorld;
  if (!Array.isArray(l.signals)) l.signals = [];
  if (typeof l.restlessness !== "number" || Number.isNaN(l.restlessness)) l.restlessness = 0;
  if (!l.watch) l.watch = freshWatch(l);
  return l;
}

export function emit(
  w: WorldState,
  kind: SignalKind,
  o: { mag?: number; depth?: number; at?: { x: number; y: number }; tag?: string } = {},
): void {
  const l = ensureSystems(w);
  if (l.signals.length >= MAX_QUEUE) return;
  l.signals.push({
    kind,
    t: l.timeSec,
    mag: o.mag ?? 1,
    depth: o.depth ?? (l.mode === "hollow" ? (l.hollow?.depth ?? 1) : 0),
    layer: l.mode === "hollow" ? "hollow" : l.mode === "home" ? "home" : "overworld",
    at: o.at ? { x: o.at.x, y: o.at.y } : { x: l.player.x, y: l.player.y },
    tag: o.tag,
  });
}

/**
 * Derive signals from state that changed since the last tick.
 *
 * The only reader of `w.watch`. Everything it inspects is authoritative state
 * that some other system already maintains, so new mechanics become world
 * events without their author doing anything.
 */
export function observe(w: LiveWorld): void {
  const p = w.watch;
  const depth = w.mode === "hollow" ? (w.hollow?.depth ?? 0) : 0;

  if (w.deaths > p.deaths) emit(w, "death", { depth: p.depth });

  if (w.extracts > p.extracts) {
    // Surfacing with a full bag is a different fact than surfacing empty.
    emit(w, "extract", { depth: p.depth, mag: 1 + Math.min(2, w.bag.length * 0.12) });
  }

  if (depth > p.depth) {
    emit(w, "descend", { depth, mag: depth >= 3 ? 1.5 : 1 });
    if (depth > p.deepest) emit(w, "depth_record", { depth, mag: Math.min(3, depth * 0.5) });
  }

  if (w.weather !== p.weather) {
    if (w.weather === "storm") emit(w, "storm");
    else if (w.weather === "fog") emit(w, "storm", { mag: 0.35, tag: "fog" });
  }

  const shelved = countShelved(w);
  if (shelved > p.shelved) emit(w, "artifact_stored", { mag: shelved, tag: "bench" });

  p.deaths = w.deaths;
  p.extracts = w.extracts;
  p.depth = depth;
  p.deepest = Math.max(p.deepest, depth);
  p.weather = w.weather;
  p.shelved = shelved;
}

export function countShelved(w: WorldState): number {
  let n = 0;
  for (const b of w.home.bench) if (b && b.id === "artifact") n += 1;
  return n;
}

export function signalsOf(w: WorldState, kind: SignalKind): Signal[] {
  return (w.signals ?? []).filter((s) => s.kind === kind);
}

export function hadSignal(w: WorldState, kind: SignalKind): boolean {
  return (w.signals ?? []).some((s) => s.kind === kind);
}

export function freshWatch(w: WorldState): LiveWorld["watch"] {
  return {
    deaths: w.deaths,
    extracts: w.extracts,
    depth: w.mode === "hollow" ? (w.hollow?.depth ?? 0) : 0,
    deepest: w.knowledge.deepest,
    weather: w.weather,
    shelved: countShelved(w),
  };
}
